import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { TitlePagesComponent } from '../../../../shared/components/title-pages-component/title-pages-component';
import { TableComponent } from '../../../../shared/components/table-component/table-component';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { UsersService } from '../../services/users.service';
import { MatDialog } from '@angular/material/dialog';
import { User } from '../../services/user.interface';
import { AppConstants } from '@/app/constants/AppConstants';
import { ClientsService } from '@/app/features/clientes/services/clients.service';
import { Client } from '@/app/features/clientes/services/client.interface';
import { SnackbarService } from '../../../../shared/services/snackbar.service';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { ConfirmDeleteDialog } from '@/app/shared/components/confirm-delete-dialog/confirm-delete-dialog';
export interface Usuario {
  id: number;
  nombre: string;
  puesto: string;
  departamento: string;
  centroTrabajo: string;
  fechaIngreso: string;
  activo: boolean;
}

export interface UsuarioTransformed extends User {
  fullName: string; // Keep fullName
  clientName: string; // Add client name property
}

@Component({
  selector: 'app-usuarios-component',
  standalone: true,
  imports: [
    CommonModule,
    TitlePagesComponent,
    TableComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    ConfirmDeleteDialog,
  ],
  templateUrl: './usuarios-component.html',
  styleUrl: './usuarios-component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class UsuariosComponent implements OnInit {
  @ViewChild('confirmDialog') confirmDialog!: TemplateRef<any>;
  protected userToDelete: UsuarioTransformed | null = null;
  protected pageTitle = 'Usuarios';
  protected showBtn: boolean = true;
  protected usuarios: UsuarioTransformed[] = [];
  protected activateMenu = true;
  protected clients: { id: string; name: string }[] = []; // Store clients
  protected selectedClient: string = '';

  private clientsService = inject(ClientsService);
  private usersService = inject(UsersService);
  private router = inject(Router);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  protected showModal: boolean = false;
  protected message!: string;

  protected pageData = {
    title: AppConstants.USUARIOS_TITLE,
    subTitle: AppConstants.USUARIOS_SUBTITLE,
  };

  ngOnInit(): void {
    this.loadClients(); // solo si hay clientes se cargan los usuarios
  }

  private loadClients(): void {
    this.clientsService.getAllClients().subscribe({
      next: (clients: Client[]) => {
        this.clients = clients.map((client) => ({
          id: client.publicId,
          name: client.name,
        }));

        this.loadUsers();
      },
    });
  }

  private loadUsers(): void {
    this.usersService.getUsers().subscribe((data) => {
      this.usuarios = data.map((user) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`, // Calculate fullName
        clientName: this.getClientName(user.attributes?.['clientName'] || ''), // Map client ID to name
        attributes: {
          clientName: user.attributes?.['clientName'] || '',
          publicId: user.attributes?.['publicId'] || '',
        },
      }));
    });
  }

  addUsuario = () => {
    this.router.navigate(['/usuarios/crear']);
  };

  editUsuario = (usuario: UsuarioTransformed) => {
    this.router.navigate(['/usuarios/editar', usuario.id]);
  };

  deleteUser = () => {
    this.usersService.deleteUser(this.userToDelete!.id).subscribe({
      next: () => {
        this.snackbarService.showSuccess('Usuario eliminado correctamente');
        this.usersService.refreshUsers();
        this.showModal = false;
      },
      error: (err) => {
        this.snackbarService.showError(
          'Error al eliminar usuario: ' + err.message
        );
      },
    });
  };

  showConfirmDeleteDialog = (user: UsuarioTransformed) => {
    this.message = 'Se eliminará al usuario : ' + user.fullName;
    this.userToDelete = user;
    this.showModal = true;
  };

  handleCancelDeleteDialog() {
    this.showModal = false;
  }

  columnDefinitions = [
    { key: 'username', displayName: 'Nombre de usuario' },
    { key: 'firstName', displayName: 'Nombre(s)' },
    { key: 'lastName', displayName: 'Apellidos' },
    { key: 'email', displayName: 'Correo electrónico' },
    { key: 'clientName', displayName: 'Cliente' }, // Changed to use clientName
    { key: 'acciones', displayName: 'Acciones' },
  ];

  private getClientName(clientId: string): string {
    if (!clientId) {
      return 'Administrador';
    }

    const client = this.clients.find((c) => c.id === clientId);
    return client ? client.name : clientId;
  }

  get filteredUsuarios(): UsuarioTransformed[] {
    if (!this.selectedClient) {
      return this.usuarios;
    }

    return this.usuarios.filter(
      (user) => user.attributes?.['publicId'] === this.selectedClient
    );
  }

  sendVerificationEmail = (item: any) => {
    this.usersService.sendVerificationEmail(item.id).subscribe({
      next: () => {
        this.snackbarService.showSuccess('Correo de verificación enviado');
      },
      error: (err) => {
        this.snackbarService.showError(
          'Error al enviar correo de verificación: ' + err.message
        );
      },
    });
  };

  options = [{ label: 'Enviar correo de verificación', action: this.sendVerificationEmail }];
}
